import { type FormEvent, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { HubBackLink } from '@/components/hub/HubBackLink';
import {
  customFieldsTemplateFromCatalog,
  lmsUsersAiPromptFromCatalog,
  lmsUsersExampleWithCustomFields,
  type LmsCustomFieldCatalogItem,
} from '@lms/shared-types';
import { useToast } from '@lms/ui';
import { api, listResource } from '@/lib/api';
import { userImportRequestErrorToast, userImportToasts } from '@/lib/user-feedback';

interface CatalogCustomField {
  name?: string;
  slug: string;
  type: string;
  options?: Array<{ value: string; label?: string; isActive?: boolean }>;
}

function downloadJson(filename: string, payload: unknown) {
  const blob = new Blob([`${JSON.stringify(payload, null, 2)}\n`], { type: 'application/json' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

async function loadCustomFieldCatalog(): Promise<LmsCustomFieldCatalogItem[]> {
  const rows: CatalogCustomField[] = [];
  const first = await listResource<CatalogCustomField>('/user-custom-fields', { page: 1, perPage: 200 });
  rows.push(...(first.data ?? []));
  const lastPage = first.meta?.lastPage ?? 1;
  for (let page = 2; page <= lastPage; page += 1) {
    const next = await listResource<CatalogCustomField>('/user-custom-fields', { page, perPage: 200 });
    rows.push(...(next.data ?? []));
  }
  return rows;
}

export interface UsersImportResult {
  format: 'lms_users';
  imported: number;
  parsed: number;
  skippedCount: number;
  failed: number;
  created: Array<{ email: string; temporaryPassword?: string }>;
  skipped: Array<{ email: string; reason: string }>;
  errors: string[];
}

function formatImportReport(result: UsersImportResult): string {
  const lines = [
    `Creados: ${result.imported}`,
    `Omitidos: ${result.skippedCount}`,
    `Errores: ${result.errors.length}`,
    '',
    'Creados:',
    ...result.created.map((row) =>
      row.temporaryPassword ? `${row.email}\t${row.temporaryPassword}` : row.email,
    ),
    '',
    'Omitidos:',
    ...result.skipped.map((row) => `${row.email}\t${row.reason}`),
    '',
    'Errores:',
    ...result.errors,
  ];
  return `${lines.join('\n')}\n`;
}

export function ImportUsersPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [content, setContent] = useState('');
  const [fileName, setFileName] = useState('');
  const [fileContent, setFileContent] = useState('');
  const [promptCopyState, setPromptCopyState] = useState<'idle' | 'copied' | 'error'>('idle');
  const [reportCopyState, setReportCopyState] = useState<'idle' | 'copied' | 'error'>('idle');
  const [result, setResult] = useState<UsersImportResult | null>(null);

  const [templateBusy, setTemplateBusy] = useState(false);

  const importMutation = useMutation({
    mutationFn: (payload: { content: string }) =>
      api<UsersImportResult>('/users/import/json', { method: 'POST', body: payload }),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      setResult(data);
      for (const item of userImportToasts(data)) toast(item);
    },
    onError: (err) => {
      setResult(null);
      toast(userImportRequestErrorToast(err));
    },
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      setFileName('');
      setFileContent('');
      return;
    }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => setFileContent(typeof reader.result === 'string' ? reader.result : '');
    reader.readAsText(file);
  };

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const finalContent = content.trim() || fileContent.trim();
    if (!finalContent) {
      toast({ title: 'Sube un archivo JSON o pega el contenido a importar.', variant: 'danger' });
      return;
    }
    importMutation.mutate({ content: finalContent });
  }

  async function downloadTemplate() {
    setTemplateBusy(true);
    try {
      const catalog = await loadCustomFieldCatalog();
      const customFields = customFieldsTemplateFromCatalog(catalog);
      downloadJson('lms-users.ejemplo.json', lmsUsersExampleWithCustomFields(customFields));
    } catch (err) {
      toast({
        title: (err as Error).message || 'No se pudo armar la plantilla con los campos actuales.',
        variant: 'danger',
      });
    } finally {
      setTemplateBusy(false);
    }
  }

  async function copyAiPrompt() {
    try {
      const catalog = await loadCustomFieldCatalog();
      await navigator.clipboard.writeText(lmsUsersAiPromptFromCatalog(catalog));
      setPromptCopyState('copied');
      window.setTimeout(() => setPromptCopyState('idle'), 2000);
    } catch {
      setPromptCopyState('error');
    }
  }

  async function copyReport() {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(formatImportReport(result));
      setReportCopyState('copied');
      window.setTimeout(() => setReportCopyState('idle'), 2000);
    } catch {
      setReportCopyState('error');
    }
  }

  function resetForm() {
    setResult(null);
    setContent('');
    setFileName('');
    setFileContent('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  const fileMeta = fileName || 'Archivos .json soportados';

  return (
    <div className="lms-aiken-import lms-site-admin-hub-page font-sans">
      <div className="mb-6 flex items-start gap-4">
        <HubBackLink to="/usuarios" label="Volver a gestión de usuarios" />
        <div>
          <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Importar usuarios JSON</h1>
          <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
            Admite campos personalizados por slug o nombre. Genera o migra el archivo con un modelo de IA para importarlo aquí.
          </p>
        </div>
      </div>

      {result ? (
        <section className="lms-aiken-import__card mb-6 p-6" aria-live="polite">
          <p className="mb-4 text-dash-on-surface">
            {result.imported} creados, {result.skippedCount} omitidos, {result.errors.length} con
            error.
          </p>
          <p className="lms-aiken-import__hint lms-aiken-import__hint--spaced">
            Las contraseñas temporales solo se muestran esta vez. Cópialas ahora si las necesitas.
          </p>
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="lms-aiken-import__btn lms-aiken-import__btn--cancel"
              onClick={() => void copyReport()}
            >
              {reportCopyState === 'copied' ? 'Informe copiado' : 'Copiar informe'}
            </button>
            {reportCopyState === 'error' ? (
              <p className="lms-aiken-import__error" role="alert">
                No se pudo copiar el informe. Inténtalo de nuevo.
              </p>
            ) : null}
            <button
              type="button"
              className="lms-aiken-import__btn lms-aiken-import__btn--cancel"
              onClick={resetForm}
            >
              Importar otro archivo
            </button>
          </div>
          {result.created.length > 0 ? (
            <table className="mb-4 w-full text-left text-sm">
              <caption className="mb-2 text-left font-medium">Creados</caption>
              <thead>
                <tr>
                  <th>Correo</th>
                  <th>Contraseña temporal</th>
                </tr>
              </thead>
              <tbody>
                {result.created.map((row) => (
                  <tr key={row.email}>
                    <td>{row.email}</td>
                    <td className="font-mono">{row.temporaryPassword ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
          {result.skipped.length > 0 ? (
            <table className="mb-4 w-full text-left text-sm">
              <caption className="mb-2 text-left font-medium">Omitidos</caption>
              <thead>
                <tr>
                  <th>Correo</th>
                  <th>Motivo</th>
                </tr>
              </thead>
              <tbody>
                {result.skipped.map((row) => (
                  <tr key={`${row.email}-${row.reason}`}>
                    <td>{row.email}</td>
                    <td>{row.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
          {result.errors.length > 0 ? (
            <table className="mb-4 w-full text-left text-sm">
              <caption className="mb-2 text-left font-medium">Errores</caption>
              <thead>
                <tr>
                  <th>Detalle</th>
                </tr>
              </thead>
              <tbody>
                {result.errors.map((line, index) => (
                  <tr key={`${index}-${line}`}>
                    <td>{line}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </section>
      ) : null}

      <form className="lms-aiken-import__card" onSubmit={handleSubmit}>
        <div className="lms-aiken-import__body">
          <div className="lms-aiken-import__field">
            <span className="lms-aiken-import__label">Plantilla e instrucciones para IA</span>
            <p className="lms-aiken-import__hint lms-aiken-import__hint--spaced">
              Descarga el ejemplo con los campos personalizados actuales, o copia el prompt para
              convertir un listado a este JSON.
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="lms-aiken-import__btn lms-aiken-import__btn--cancel"
                onClick={() => void downloadTemplate()}
                disabled={templateBusy}
              >
                {templateBusy ? 'Preparando plantilla…' : 'Descargar plantilla'}
              </button>
              <button
                type="button"
                className="lms-aiken-import__btn lms-aiken-import__btn--cancel"
                onClick={() => void copyAiPrompt()}
              >
                {promptCopyState === 'copied' ? 'Prompt copiado' : 'Copiar prompt para IA'}
              </button>
            </div>
            {promptCopyState === 'error' ? (
              <p className="lms-aiken-import__error" role="alert">
                No se pudo copiar. Inténtalo de nuevo o copia el texto a mano.
              </p>
            ) : null}
          </div>

          <div className="lms-aiken-import__divider" />

          <div className="lms-aiken-import__field">
            <span className="lms-aiken-import__label">Archivo JSON</span>
            <p className="lms-aiken-import__hint lms-aiken-import__hint--spaced">
              Archivo <code>lms-users</code> generado por IA o armado a partir de la plantilla.
            </p>
            <label htmlFor="import_file" className="lms-aiken-import__dropzone group">
              <span
                className="material-symbols-outlined lms-aiken-import__dropzone-icon"
                style={{ fontVariationSettings: "'wght' 300" }}
                aria-hidden="true"
              >
                upload_file
              </span>
              <div className="lms-aiken-import__dropzone-text">
                <span className="lms-aiken-import__dropzone-link">Selecciona un archivo</span>
                <span>o arrastra y suelta aquí</span>
              </div>
              <p className="lms-aiken-import__dropzone-meta">{fileMeta}</p>
              <input
                ref={fileInputRef}
                id="import_file"
                name="import_file"
                type="file"
                accept=".json,application/json"
                className="sr-only"
                onChange={handleFileChange}
              />
            </label>
          </div>

          <div className="lms-aiken-import__or">
            <span className="lms-aiken-import__or-line" />
            <span className="lms-aiken-import__or-label">O</span>
            <span className="lms-aiken-import__or-line" />
          </div>

          <div className="lms-aiken-import__field">
            <label htmlFor="import_content" className="lms-aiken-import__label">
              Contenido JSON
            </label>
            <p className="lms-aiken-import__hint lms-aiken-import__hint--spaced">
              Pega directamente el JSON <code>lms-users</code>. Si hay texto aquí, tiene prioridad
              sobre el archivo.
            </p>
            <textarea
              id="import_content"
              name="import_content"
              rows={8}
              value={content}
              onChange={(event) => setContent(event.target.value)}
              className="lms-aiken-import__textarea"
              placeholder='{"format":"lms-users","schemaVersion":1,"users":[]}'
            />
          </div>

        </div>

        <div className="lms-aiken-import__footer">
          <Link to="/usuarios" className="lms-aiken-import__btn lms-aiken-import__btn--cancel">
            Cancelar
          </Link>
          <button
            type="submit"
            className="lms-aiken-import__btn lms-aiken-import__btn--submit"
            disabled={importMutation.isPending}
          >
            {importMutation.isPending ? 'Importando…' : 'Importar usuarios'}
          </button>
        </div>
      </form>
    </div>
  );
}
